import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, Field, Modal, PageHeader, Spinner, StatusBadge } from '../components/ui';
import { VariantPreview } from './ContentStudioPage';
import { VariantImageControl } from '../components/VariantImage';
import { VariantVideoControl } from '../components/VariantVideo';
import { SocialSeoSummary } from '../components/SocialSeoSummary';
import { QualityScores } from '../components/Quality';
import { formatDate } from '@kontentowo/shared';
import type { ContentVariant } from '@kontentowo/shared';

function PostUrlLink({ resultId, externalId, platform }: { resultId: string; externalId: string | null; platform: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolve = async () => {
    if (!externalId) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/publish/${resultId}/url`, { method: 'POST' });
      const d = await res.json();
      setUrl(d.postUrl ?? null);
    } catch { /* noop */ }
    finally { setBusy(false); }
  };
  if (url) return <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{platform} (post)</a>;
  return externalId ? (
    <button className="btn btn--sm" onClick={resolve} disabled={busy}>{busy ? '...' : 'URL'}</button>
  ) : <span className="muted" style={{ fontSize: 12 }}>—</span>;
}

export function ContentDetailPage() {
  const { id = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.content.get(id), [id]);
  const { data: contentVideoJobs, reload: reloadContentVideoJobs } = useAsync(() => api.content.videoJobs(id), [id]);
  const { data: queueOptions, reload: reloadQueueOptions } = useAsync(() => api.content.queueOptions(id), [id]);
  const audit = useAsync(() => api.audit('content', id, 20), [id]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [videoMessage, setVideoMessage] = useState<string | null>(null);
  const [remotionMessage, setRemotionMessage] = useState<string | null>(null);
  const [variantMessage, setVariantMessage] = useState<string | null>(null);
  const [remotionTemplate, setRemotionTemplate] = useState('MoneyScenario');
  const [remotionLength, setRemotionLength] = useState<'SHORT' | 'STANDARD' | 'DEEP'>('SHORT');

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regLength, setRegLength] = useState('medium');
  const [regTone, setRegTone] = useState('');
  const [regInstructions, setRegInstructions] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regMsg, setRegMsg] = useState<string | null>(null);
  const regenerate = async () => {
    setRegBusy(true); setRegMsg(null);
    try {
      const r = await api.content.regenerate(id, { length: regLength, tone: regTone || undefined, instructions: regInstructions || undefined });
      setRegMsg(`Zregenerowano: ${r.updated} wariantów.`);
      setRegOpen(false);
      reload();
    } catch (e) { setRegMsg((e as Error).message); }
    finally { setRegBusy(false); }
  };
  const [scheduledAt, setScheduledAt] = useState(defaultScheduleValue());
  const [editing, setEditing] = useState<ContentVariant | null>(null);
  const [activePlatform, setActivePlatform] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [simBusy, setSimBusy] = useState(false);
  const [sim, setSim] = useState<Awaited<ReturnType<typeof api.content.similarity>> | null>(null);

  const runSim = async () => {
    const cid = data?.item?.id;
    if (!cid) return;
    setSimBusy(true);
    setActionError(null);
    try {
      setSim(await api.content.similarity(cid));
    } catch (err) {
      setActionError(err);
    } finally {
      setSimBusy(false);
    }
  };

  const prepareFboVideo = async () => {
    setBusy(true); setActionError(null); setVideoMessage(null);
    try {
      const result = await api.content.createFboVideo(id, 'MiniStory');
      setVideoMessage(`Reel dodany do kolejki: ${result.job.id}. Status zobaczysz w FBO Video Review Queue.`);
    } catch (err) { setActionError(err); }
    finally { setBusy(false); }
  };
  const prepareRemotionVideo = async () => {
    setBusy(true); setActionError(null); setRemotionMessage(null);
    try {
      const result = await api.content.createRemotionVideo(id, remotionTemplate, remotionLength);
      setRemotionMessage(`Uniwersalny Remotion Reel dodany do kolejki: ${result.job.id}. Podgląd będzie w tej sekcji oraz w Video Review Queue.`);
      reloadContentVideoJobs();
    } catch (err) { setActionError(err); }
    finally { setBusy(false); }
  };
  const prepareRemotionVariants = async () => {
    setBusy(true); setActionError(null); setVariantMessage(null);
    try {
      const results = await api.content.createRemotionVariants(id, remotionTemplate, remotionLength);
      setVariantMessage(`Utworzono ${results.length} wersje Remotion: ${results.map((result) => result.spec.platform).join(', ')}. Znajdziesz je w Video Review Queue.`);
      reloadContentVideoJobs();
    } catch (err) { setActionError(err); }
    finally { setBusy(false); }
  };
  const scheduleViaBrandQueue = async () => {
    setBusy(true); setActionError(null);
    try {
      const result = await api.content.scheduleQueue(id);
      setVideoMessage(`Dodano do kolejki brandu: ${result.queue.label} (${result.queue.localTime}).`);
      reload(); reloadQueueOptions();
    } catch (err) { setActionError(err); }
    finally { setBusy(false); }
  };
  const cancelVideoJob = async (jobId: string) => { setBusy(true); setActionError(null); try { await api.content.cancelVideoJob(jobId); reloadContentVideoJobs(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const regenerateVideoJob = async (jobId: string) => { setBusy(true); setActionError(null); try { await api.content.regenerateVideoJob(jobId); setRemotionMessage('Nowy render dodany do kolejki.'); reloadContentVideoJobs(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const approveVideoJob = async (jobId: string) => { setBusy(true); setActionError(null); try { await api.fbo.reviewVideo(jobId, 'APPROVED'); setRemotionMessage('Video zatwierdzone. Możesz teraz opublikować je na social media.'); reloadContentVideoJobs(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const publishVideoJob = async (jobId: string, platform?: string) => { if (!confirm(platform ? `Opublikować Reel tylko na ${platform}?` : 'Opublikować ten Reel na skonfigurowanych kanałach social?')) return; setBusy(true); setActionError(null); try { const result = await api.fbo.publishVideo(jobId, platform); setRemotionMessage(result.status === 'PUBLISHED' ? (platform ? `Reel opublikowany na ${platform}.` : 'Reel opublikowany na kanałach social.') : `Publikacja zakończona statusem: ${result.status}`); reloadContentVideoJobs(); reload(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const retryPublication = async () => { if (!confirm('Ponowić nieudaną publikację na skonfigurowanych kanałach social?')) return; setBusy(true); setActionError(null); try { const result = await api.content.retryPublish(id); setVideoMessage(`Ponowiono publikację: ${result.results.map((item) => `${item.platform}=${item.status}`).join(', ')}`); reload(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const retryVariantPublication = async (variant: ContentVariant) => { if (!confirm(`Ponowić publikację tylko na ${variant.platform}? Pozostałe kanały nie zostaną wysłane ponownie.`)) return; setBusy(true); setActionError(null); try { const result = await api.content.retryPublishVariant(id, variant.id); setVideoMessage(`Ponowiono tylko ${result.retriedVariant}: ${result.results.map((entry) => `${entry.platform}=${entry.status}`).join(', ')}`); reload(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const reviewPlatformVariant = async (variant: ContentVariant, decision: 'APPROVED' | 'SKIPPED') => { setBusy(true); setActionError(null); try { const result = await api.content.reviewVariant(id, variant.id, decision); const approval = result.variants.find((entry) => entry.id === variant.id)?.metadata.approvalStatus ?? decision; setVariantMessage(`${variant.platform}: ${approval}. ${result.approval.approvedCount} approved · ${result.approval.pendingCount} pending.`); reload(); } catch (err) { setActionError(err); } finally { setBusy(false); } };
  const deleteVideo = async (assetId: string) => {
    if (!confirm('Usunąć ten render MP4? Job pozostanie w historii, ale plik i harmonogram publikacji zostaną unieważnione.')) return;
    setBusy(true); setActionError(null);
    try { await api.mediaDeleteVideo(assetId); setVideoMessage('Render został usunięty.'); reload(); } catch (err) { setActionError(err); } finally { setBusy(false); }
  };
  const deleteContent = async () => {
    if (!confirm('Usunąć ten content, warianty, harmonogramy i powiązane media? Tej operacji nie można cofnąć.')) return;
    setBusy(true); setActionError(null);
    try { await api.content.remove(id); window.location.assign('/content'); } catch (err) { setActionError(err); } finally { setBusy(false); }
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorAlert error={error} />;
  if (!data) return null;

  const { item, variants, brand, schedules, results } = data;
  // A retry creates a new publish result. Show only the newest result per
  // platform in the main status table; older failures remain in the audit
  // history but must not look like the current state of the channel.
  const latestPublishResults = Array.from(
    results.reduce((latest, result) => {
      const previous = latest.get(result.platform);
      if (!previous || new Date(result.createdAt).getTime() >= new Date(previous.createdAt).getTime()) {
        latest.set(result.platform, result);
      }
      return latest;
    }, new Map<string, (typeof results)[number]>()).values(),
  );
  const latestCompletedVideo = contentVideoJobs?.find((job) => job.status === 'COMPLETED');

  return (
    <>
      <PageHeader
        title={item.topic}
        subtitle={`${brand?.name ?? 'Brand'} · ${item.language} · ${item.contentType}`}
        actions={
          <>
            <Link className="btn" to="/review">
              ← Review
            </Link>
            <Button variant="ghost" disabled={busy} onClick={() => run(async () => { await api.content.quality(item.id); })}>
              Quality check
            </Button>
            <Button variant="ghost" disabled={simBusy} onClick={runSim}>
              {simBusy ? 'Sprawdzanie…' : 'Podobieństwo'}
            </Button>
            {(item.status === 'DRAFT' || item.status === 'REVIEW' || item.status === 'GENERATED') && (
              <>
                <Button variant="success" disabled={busy} onClick={() => run(() => api.content.approve(item.id))}>
                  {brand?.slug === 'bloodcrimes' ? 'Approve all platforms' : 'Approve'}
                </Button>
                <Button variant="danger" disabled={busy} onClick={() => run(() => api.content.reject(item.id))}>
                  Reject
                </Button>
              </>
            )}
            {item.status === 'APPROVED' && (
              <Button variant="primary" disabled={busy} onClick={() => setScheduleOpen(true)}>
                Schedule
              </Button>
            )}
            {item.status === 'APPROVED' && queueOptions?.enabled && (
              <Button variant="success" disabled={busy} onClick={scheduleViaBrandQueue}>
                Dodaj do kolejki brandu
              </Button>
            )}
            {(item.status === 'SCHEDULED' || item.status === 'APPROVED') && (
              <Button variant="success" disabled={busy} onClick={() => run(() => api.content.publish(item.id))}>
                Publish
              </Button>
            )}
            {item.status === 'FAILED' && <Button variant="primary" disabled={busy} onClick={retryPublication}>Retry publication</Button>}
          </>
        }
      />

      <ErrorAlert error={actionError} />
      {videoMessage && <div className="alert alert--success">{videoMessage}</div>}
      {remotionMessage && <div className="alert alert--success">{remotionMessage}</div>}
      {variantMessage && <div className="alert alert--success">{variantMessage}</div>}

      <div className="grid grid--2">
        <Card title="POST / ARTYKUŁ — workflow">
          <div className="stack">
            <div className="inline">
              <StatusBadge status={item.status} />
              <Badge tone="info">{item.goal ?? 'no goal'}</Badge>
            </div>
            <div className="btn-row" style={{ marginTop: 4, padding: '10px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
              {(item.status === 'DRAFT' || item.status === 'REVIEW' || item.status === 'GENERATED') && <>
                <Button size="sm" variant="success" disabled={busy} onClick={() => run(() => api.content.approve(item.id))}>{brand?.slug === 'bloodcrimes' ? 'Approve all platforms' : 'Approve post'}</Button>
                <Button size="sm" variant="danger" disabled={busy} onClick={() => run(() => api.content.reject(item.id))}>{brand?.slug === 'bloodcrimes' ? 'Reject all platforms' : 'Reject post'}</Button>
              </>}
              {item.status === 'APPROVED' && <Button size="sm" variant="primary" disabled={busy} onClick={() => setScheduleOpen(true)}>Schedule post</Button>}
              {item.status === 'APPROVED' && queueOptions?.enabled && <Button size="sm" variant="success" disabled={busy} onClick={scheduleViaBrandQueue}>Dodaj do kolejki brandu</Button>}
              {(item.status === 'SCHEDULED' || item.status === 'APPROVED') && <Button size="sm" variant="success" disabled={busy} onClick={() => run(() => api.content.publish(item.id))}>Publish post</Button>}
              {item.status === 'FAILED' && <Button size="sm" variant="primary" disabled={busy} onClick={retryPublication}>Retry publication</Button>}
              {!['REVIEW', 'GENERATED', 'APPROVED', 'SCHEDULED', 'FAILED'].includes(item.status) && <span className="muted" style={{ fontSize: 12 }}>Akcje publikacji pojawią się po przejściu do Review.</span>}
            </div>
            {item.brief && <div className="pre">{item.brief}</div>}
            <div className="muted">Provider: {item.providerId ?? '—'} · Model: {item.modelId ?? '—'}</div>
            <div className="muted">Utworzono: {formatDate(item.createdAt)}</div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <Button size="sm" variant="danger" disabled={busy} onClick={deleteContent}>Usuń content</Button>
            </div>
            {data.aiCost.count > 0 && (
              <div className="alert alert--info" style={{ margin: 0 }}>
                Koszt AI: ${data.aiCost.totalCost.toFixed(6)} · {data.aiCost.count} gener. · {data.aiCost.totalInput}/{data.aiCost.totalOutput} tokenów
              </div>
            )}
            {data.generations.length > 0 && (
              <table className="table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Model</th>
                    <th>Typ</th>
                    <th>Koszt</th>
                  </tr>
                </thead>
                <tbody>
                  {data.generations.map((g) => (
                    <tr key={g.id}>
                      <td className="mono">{g.providerKey}</td>
                      <td className="mono">{g.modelId ?? '—'}</td>
                      <td>
                        <Badge tone="neutral">{g.type}</Badge>
                      </td>
                      <td>${(g.estimatedCost ?? 0).toFixed(6)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn--sm" onClick={() => setRegOpen(true)}>Regeneruj z opcjami</button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              Video trafi do Video Review Queue. Generowanie nie publikuje automatycznie.
            </div>
            <h3 style={{ marginTop: 18, marginBottom: 4 }}>VIDEO / REELS</h3>
            <div className="alert alert--info" style={{ marginTop: 8 }}>
              <strong>Remotion Reels</strong>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Wybierz layout. Render odbywa się na VPS `vps-01`, z polskim lektorem Piper, a gotowy MP4 trafia do R2.</div>
              <div className="form-row" style={{ marginTop: 10 }}>
                <select className="select" value={remotionTemplate} onChange={(event) => setRemotionTemplate(event.target.value)} disabled={busy}>
                  {['MoneyScenario', 'ChallengePromo', 'FinanceFact', 'ThreeTips', 'MythFact', 'ToolDemo', 'BudgetioFeature', 'BeforeAfterBudget', 'NumberExplainer', 'MiniStory', 'QuoteStat', 'ChecklistVideo'].map((template) => <option key={template} value={template}>{template}</option>)}
                </select>
                <select className="select" value={remotionLength} onChange={(event) => setRemotionLength(event.target.value as typeof remotionLength)} disabled={busy}>
                  <option value="SHORT">SHORT · 12–15 s</option>
                  <option value="STANDARD">STANDARD · 20–25 s</option>
                  <option value="DEEP">DEEP · 30–45 s</option>
                </select>
                <Button size="sm" variant="primary" disabled={busy} onClick={prepareRemotionVideo}>{busy ? 'Kolejkuję…' : 'Generuj uniwersalny Reel'}</Button>
                <Button size="sm" disabled={busy} onClick={prepareRemotionVariants}>Generuj wersje platformowe</Button>
                {latestCompletedVideo && latestCompletedVideo.reviewStatus === 'APPROVED' && <Button size="sm" variant="success" disabled={busy} onClick={() => publishVideoJob(latestCompletedVideo.id)}>Publish social</Button>}
                {latestCompletedVideo && latestCompletedVideo.reviewStatus === 'APPROVED' && latestPublishResults.some((result) => result.platform === 'TIKTOK' && result.status === 'FAILED') && <Button size="sm" variant="primary" disabled={busy} onClick={() => publishVideoJob(latestCompletedVideo.id, 'TIKTOK')}>Retry TikTok</Button>}
                {latestCompletedVideo && latestCompletedVideo.reviewStatus !== 'APPROVED' && <Button size="sm" variant="success" disabled={busy} onClick={() => approveVideoJob(latestCompletedVideo.id)}>Approve video</Button>}
              </div>
            </div>
            <div className="alert alert--info" style={{ marginTop: 8 }}>
              <strong>Video jobs tego wpisu</strong>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Każdy job dotyczy wyłącznie tego contentu. `Generate Platform Variants` tworzy osobne rendery: Instagram, TikTok, YouTube Shorts i Facebook.</div>
              <div className="stack" style={{ marginTop: 8 }}>
                {!contentVideoJobs?.length && <span className="muted">Brak aktywnych ani historycznych renderów.</span>}
                {contentVideoJobs?.map((job) => <div key={job.id} className="inline" style={{ justifyContent: 'space-between', borderBottom: '1px solid var(--border)', padding: '7px 0' }}><span><Badge tone={job.status === 'COMPLETED' ? 'success' : job.status === 'FAILED' ? 'danger' : job.status === 'CANCELLED' ? 'neutral' : 'warning'}>{job.status}</Badge> <strong>{job.spec?.platform ?? 'MASTER'}</strong> <span className="muted">{job.progress}% · {job.id}</span>{job.errorMessage && <span className="error"> · {job.errorMessage}</span>}</span><span className="btn-row">{['QUEUED', 'CLAIMED', 'PREPARING', 'RENDERING'].includes(job.status) && <Button size="sm" variant="danger" disabled={busy} onClick={() => cancelVideoJob(job.id)}>Cancel</Button>}{job.status === 'COMPLETED' && job.reviewStatus !== 'APPROVED' && <Button size="sm" variant="success" disabled={busy} onClick={() => approveVideoJob(job.id)}>Approve video</Button>}{job.status === 'COMPLETED' && job.reviewStatus === 'APPROVED' && <Button size="sm" variant="primary" disabled={busy} onClick={() => publishVideoJob(job.id)}>Publish social</Button>}{['COMPLETED', 'FAILED', 'CANCELLED'].includes(job.status) && <Button size="sm" disabled={busy} onClick={() => regenerateVideoJob(job.id)}>Regenerate</Button>}</span></div>)}
              </div>
            </div>
            {data.assets.filter((asset) => asset.type === 'VIDEO').length > 0 && <div className="alert alert--info" style={{ marginTop: 8 }}>
              <strong>Social Video / Reels — tylko ten wpis</strong>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Każdy render jest przypisany do tego contentu. Warianty platformowe są osobnymi plikami — nie są automatycznie dodawane do innych wpisów.</div>
              <div className="stack" style={{ marginTop: 8 }}>
                {data.assets.filter((asset) => asset.type === 'VIDEO').map((asset) => <div key={asset.id}><video src={`/api/media/${asset.id}/file`} controls style={{ width: 'min(280px, 100%)', maxHeight: 500, borderRadius: 8, display: 'block' }} /><div className="inline" style={{ justifyContent: 'space-between', marginTop: 4 }}><span className="muted" style={{ fontSize: 12 }}>{asset.variantId ? `wariant: ${variants.find((variant) => variant.id === asset.variantId)?.platform ?? asset.variantId}` : 'master / ręczny wybór kanału'} · {asset.provider} · {asset.duration ? `${asset.duration}s` : 'video'} · {asset.id}</span><Button size="sm" variant="danger" disabled={busy} onClick={() => deleteVideo(asset.id)}>Delete video</Button></div></div>)}
              </div>
            </div>}
            {item.status === 'FAILED' && (
              <div className="alert alert--danger">
                Publikacja nie powiodła się:
                <div className="stack" style={{ marginTop: 6 }}>
                  {data.results.filter((r) => r.status !== 'SUCCESS').map((r) => (
                    <div key={r.id} style={{ fontSize: 12.5 }}>
                      <b>{r.platform}</b>: {r.message ?? 'brak powodu'} {r.externalId ? `(external: ${r.externalId})` : ''}
                    </div>
                  ))}
                  {item.reviewNotes && <div className="muted">{item.reviewNotes}</div>}
                </div>
              </div>
            )}
            {item.reviewNotes && <div className="alert alert--warning">{item.reviewNotes}</div>}
            {audit.data && audit.data.length > 0 && (
              <>
                <div className="divider" />
                <div style={{ fontWeight: 650, fontSize: 13 }}>Historia akcji</div>
                <div className="stack" style={{ marginTop: 6 }}>
                  {audit.data.map((a) => (
                    <div key={a.id} className="inline" style={{ justifyContent: 'space-between' }}>
                      <span className="mono" style={{ fontSize: 12 }}>{a.action}</span>
                      <span className="muted" style={{ fontSize: 12 }}>{formatDate(a.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {sim && (
              sim.flagged ? (
                <div className="alert alert--warning" style={{ marginTop: 8 }}>
                  Content Diversity: podobieństwo <b>{Math.round(sim.highest * 100)}%</b> (próg {Math.round(sim.threshold * 100)}%). Rozważ rewrite.
                  <div className="stack" style={{ marginTop: 8 }}>
                    {sim.matches.slice(0, 3).map((m) => (
                      <Link key={m.contentId} to={`/content/${m.contentId}`}>
                        {Math.round(m.score * 100)}% · {m.text.slice(0, 60)}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="alert alert--success" style={{ marginTop: 8 }}>
                  Content Diversity OK — brak podobnych treści (najwyższe {Math.round(sim.highest * 100)}%).
                </div>
              )
            )}
          </div>
        </Card>

        <Card title="PUBLISHING — status kanałów">
          {schedules.length === 0 && latestPublishResults.length === 0 && <p className="muted">Brak zaplanowanych publikacji.</p>}
          {schedules.length > 0 && (
            <div className="stack" style={{ marginBottom: 12 }}>
              {schedules.map((s) => (
                <div key={s.id} className="inline" style={{ justifyContent: 'space-between' }}>
                  <span>{formatDate(s.scheduledAt)}</span>
                  <Badge tone={s.status === 'SENT' ? 'success' : s.status === 'FAILED' ? 'danger' : 'info'}>{s.status}</Badge>
                </div>
              ))}
            </div>
          )}
          {latestPublishResults.length > 0 && (
            <div className="alert alert--info" style={{ marginBottom: 10 }}>
              Status pokazuje ostatnią próbę dla każdego kanału. <strong>SUCCESS / scheduled</strong> oznacza, że Sociamonials przyjął publikację do kolejki — nie musi być jeszcze widoczna na profilu.
            </div>
          )}
          {latestPublishResults.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Platforma</th>
                  <th>Status</th>
                  <th>External ID</th>
                  <th>Powód</th>
                  <th>Post URL</th>
                </tr>
              </thead>
              <tbody>
                {latestPublishResults.map((r) => (
                  <tr key={r.id}>
                    <td>{r.platform}</td>
                    <td>
                      <Badge tone={r.status === 'FAILED' ? 'danger' : r.status === 'PARTIAL' ? 'warning' : /scheduled/i.test(r.message ?? '') ? 'info' : 'success'}>
                        {r.status === 'SUCCESS' && /scheduled/i.test(r.message ?? '') ? 'QUEUED / SCHEDULED' : r.status}
                      </Badge>
                    </td>
                    <td className="mono">{r.externalId ?? '—'}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {r.message ?? (r.status === 'SUCCESS' ? '—' : 'brak powodu')}
                    </td>
                    <td>
                      <PostUrlLink resultId={r.id} externalId={r.externalId} platform={r.platform} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 20 }}>
        <h2>SOCIAL MEDIA — warianty kanałów ({variants.length})</h2>
        <p className="muted">Każdy kanał ma osobny tekst, CTA i media. Akcje publikacji oraz retry są dostępne w sekcji workflow powyżej.</p>
        {brand?.slug === 'bloodcrimes' && variants.some((variant) => variant.metadata.approvalMode === 'PER_VARIANT') && <p className="muted">Approval kanałów: {variants.filter((variant) => variant.metadata.approvalStatus === 'APPROVED').length} approved · {variants.filter((variant) => variant.metadata.approvalStatus === 'PENDING').length} pending · {variants.filter((variant) => variant.metadata.approvalStatus === 'SKIPPED').length} skipped. Publikacja obejmie wyłącznie approved.</p>}
        {variantMessage && <div className="notice">{variantMessage}</div>}
        <div className="chips" role="tablist" aria-label="Platform variants" style={{ marginTop: 12 }}>
          {variants.map((variant) => <button type="button" role="tab" aria-selected={(activePlatform ?? variants[0]?.platform) === variant.platform} className={`chip${(activePlatform ?? variants[0]?.platform) === variant.platform ? ' chip--active' : ''}`} key={variant.id} onClick={() => setActivePlatform(variant.platform)}>{variant.platform}</button>)}
        </div>
        <div style={{ marginTop: 12 }}>
          {variants.filter((variant) => variant.platform === (activePlatform ?? variants[0]?.platform)).map((v) => (
            <div key={v.id}>
              <VariantPreview variant={v} />
              <SocialSeoSummary variant={v} />
              {brand?.slug === 'bloodcrimes' && !['SCHEDULED', 'PUBLISHING', 'PUBLISHED', 'ARCHIVED'].includes(item.status) && <div className="btn-row"><Badge tone={v.metadata.approvalStatus === 'APPROVED' ? 'success' : v.metadata.approvalStatus === 'SKIPPED' || v.metadata.approvalStatus === 'REJECTED' ? 'neutral' : 'warning'}>{v.metadata.approvalMode === 'PER_VARIANT' ? String(v.metadata.approvalStatus ?? 'PENDING') : item.status === 'APPROVED' ? 'APPROVED' : 'PENDING'}</Badge><Button size="sm" variant="success" disabled={busy} onClick={() => reviewPlatformVariant(v, 'APPROVED')}>Approve {v.platform}</Button>{v.metadata.approvalStatus !== 'SKIPPED' && <Button size="sm" disabled={busy} onClick={() => reviewPlatformVariant(v, 'SKIPPED')}>Skip {v.platform}</Button>}</div>}
              <QualityScores scores={v.metadata?.quality as never} />
              <VariantImageControl brandId={item.brandId} contentId={item.id} variant={v} existing={data.assets.find((a) => a.variantId === v.id && a.type === 'IMAGE')} />
              <VariantVideoControl brandId={item.brandId} contentId={item.id} variant={v} existing={data.assets.find((a) => a.variantId === v.id && a.type === 'VIDEO')} />
              <div className="btn-row" style={{ marginTop: 8 }}>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(v);
                    setEditBody(v.body);
                  }}
                >
                  Edit
                </Button>
                {latestPublishResults.find((result) => result.platform === v.platform)?.status !== 'SUCCESS' && (
                  <Button size="sm" variant="primary" disabled={busy} onClick={() => retryVariantPublication(v)}>
                    Retry {v.platform} only
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal
        title="Zaplanuj publikację"
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        footer={
          <>
            <Button onClick={() => setScheduleOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => run(async () => {
                await api.content.schedule(item.id, new Date(scheduledAt).toISOString());
                setScheduleOpen(false);
              })}
            >
              Zaplanuj
            </Button>
          </>
        }
      >
        <Field label="Data i godzina">
          <input className="input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        title="Edytuj wariant"
        open={editing !== null}
        onClose={() => setEditing(null)}
        wide
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={busy || !editing}
              onClick={() =>
                run(async () => {
                  if (editing) await api.content.editVariant(item.id, editing.id, { body: editBody });
                  setEditing(null);
                })
              }
            >
              Zapisz
            </Button>
          </>
        }
      >
        {editing && (
          <>
            <div className="inline" style={{ marginBottom: 10 }}>
              <Badge tone="info">{editing.platform}</Badge>
              <Badge tone="neutral">{editing.kind}</Badge>
            </div>
            <textarea className="textarea" style={{ minHeight: 220 }} value={editBody} onChange={(e) => setEditBody(e.target.value)} />
          </>
        )}
      </Modal>
    </>
  );
}

function defaultScheduleValue(): string {
  const d = new Date(Date.now() + 86400000);
  d.setMinutes(0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export { defaultScheduleValue };
